import React from "react";
import { View, StyleSheet, useWindowDimensions } from "react-native";
import { Image } from "expo-image";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";
import { TeamworksIcon } from "./TeamworksLogo";
import { Spacer } from "./ViewComponents";
import { CurrentDateTime } from "./CurrentDateTime";
import { useNextMatch, useShowWeather } from "../providers/ConfigProvider";
import { WeatherWidget } from "./WeatherWidget";
import { MatchCountdown } from "./MatchCountdown";

const NARROW_BREAKPOINT = 1200;

const makeStyles = (theme: ThemeOptions, isPortrait: boolean) =>
  StyleSheet.create({
    topBar: {
      flexDirection: "column",
      paddingHorizontal: theme.spacing.regular,
      paddingTop: isPortrait ? theme.spacing.small : theme.spacing.regular,
      paddingBottom: isPortrait ? theme.spacing.xsmall : theme.spacing.medium,
    },
    primaryRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      columnGap: isPortrait ? theme.spacing.small : 0,
    },
    logo: {
      width: isPortrait ? theme.iconSize.larger : theme.iconSize.xlarge,
      height: isPortrait ? theme.iconSize.larger : theme.iconSize.xlarge,
    },
    leftTopBar: {
      flex: 1,
      flexDirection: "row",
      flexGrow: 1,
      flexShrink: 1,
      alignItems: "center",
    },
    rightTopBar: {
      flex: 1,
      flexDirection: "row",
      flexGrow: isPortrait ? 1 : 2,
      flexShrink: 1,
      justifyContent: "flex-end",
      alignItems: "center",
    },
    matchCountdownRow: {
      flexDirection: "row",
      marginTop: theme.spacing.small,
    },
  });

type TopBarProps = {
  orientation?: "portrait" | "landscape";
};

export const TopBar = ({ orientation }: TopBarProps = {}) => {
  const theme = useTheme();
  const { width, height } = useWindowDimensions();
  const isPortrait =
    orientation === "portrait" || (orientation == null && height > width);
  const styles = makeStyles(theme, isPortrait);
  const nextMatch = useNextMatch();
  const showWeather = useShowWeather();
  const matchOnOwnRow =
    nextMatch != null && isPortrait && width < NARROW_BREAKPOINT;

  const inlineWidgets = [
    showWeather ? <WeatherWidget /> : null,
    !matchOnOwnRow && nextMatch != null ? <MatchCountdown /> : null,
  ].filter(Boolean) as React.JSX.Element[];

  return (
    <View style={styles.topBar}>
      <View style={styles.primaryRow}>
        <View style={styles.leftTopBar}>
          {theme.customHeaderImageUrl != null ? (
            <Image
              style={[styles.logo]}
              source={{ uri: theme.customHeaderImageUrl }}
              contentFit="contain"
            />
          ) : theme.logoUrl != null ? (
            <Image
              style={[styles.logo]}
              source={{ uri: theme.logoUrl }}
              contentFit="contain"
            />
          ) : (
            <TeamworksIcon style={styles.logo} />
          )}
          <Spacer size="medium" />
          <CurrentDateTime compact={isPortrait} />
        </View>
        <View style={styles.rightTopBar}>
          {inlineWidgets.map((widget, index) => (
            <View
              key={index}
              style={{
                marginRight:
                  index === inlineWidgets.length - 1 ? 0 : theme.spacing.small,
              }}
            >
              {widget}
            </View>
          ))}
        </View>
      </View>
      {matchOnOwnRow && (
        <View style={styles.matchCountdownRow}>
          <MatchCountdown />
        </View>
      )}
    </View>
  );
};
